import { useMutation, useQueryClient } from '@tanstack/react-query';
import { PractitionerNotePreferences, PractitionerSettings } from 'src/models';
import { ambientApi } from 'src/services';
import { showToastError } from 'src/utilities/errortoast';
import { showToastSuccess } from 'src/utilities/toast';

type UpdateSuperadminPractitionerSettingsParams = {
  practitioner_id?: number;
  settings: Partial<
    Omit<
      PractitionerSettings,
      | 'created_at'
      | 'created_by'
      | 'last_modified'
      | 'last_modified_by'
      | 'ambient_version'
      | 'settings'
      | 'note_preferences'
    >
  > &
    Partial<PractitionerNotePreferences>;
};

type SuperadminApiConfig = {
  headers?: Record<string, string>;
};

export const useUpdateSuperadminPractitionerSettings = (
  apiConfig: SuperadminApiConfig = {}
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['updateSuperadminPractitionerSettings'],
    onMutate: () => {
      const orgUuid = apiConfig?.headers?.['Impersonate-Org-UUID'] ?? null;
      return { orgUuid };
    },
    mutationFn: (params: UpdateSuperadminPractitionerSettingsParams) => {
      const { practitioner_id, settings } = params;
      const body = practitioner_id
        ? { practitioner_id, ...settings }
        : settings;
      return ambientApi
        .post<PractitionerSettings>(
          '/practitioners/settings/superadmin',
          body,
          apiConfig
        )
        .then((res) => res.data);
    },
    onError: () => {
      showToastError('Failed to update settings');
    },
    onSuccess: (_, variables, context) => {
      if (variables.practitioner_id) {
        showToastSuccess('Practitioner settings updated successfully');
      }

      queryClient.invalidateQueries({
        queryKey: ['superadminPractitionerSettings', context?.orgUuid ?? null],
      });
    },
  });
};
